import { ref, Ref } from 'vue';
import { MasterDataApiCaller } from '../utils/MasterDataApiCaller';

/**
 * Session-cached master data composable for direct order statuses
 * Loads master data once per browser session and caches them in memory
 *
 * Usage:
 * ```typescript
 * import { useMasterData } from '@/composables/useMasterData';
 *
 * const { directOrderStatus, loadAllMasterData } = useMasterData();
 *
 * // In setup()
 * onMounted(async () => {
 *     await loadAllMasterData();
 * });
 *
 * // Use in template or methods
 * item.directOrderStatus === directOrderStatus.value.pending
 * ```
 */

// Global state - persists across component instances for session duration
const directOrderStatus: Ref<any> = ref({});
const courierList: Ref<string[]> = ref([]);

// Loading state
const isLoading: Ref<boolean> = ref(false);
const isLoaded: Ref<boolean> = ref(false);
const loadError: Ref<string> = ref('');

/**
 * Composable function for accessing master data
 * Returns reactive refs that are shared across all component instances
 */
export function useMasterData() {
    /**
     * Load all master data from API
     * Only loads once per session - subsequent calls return immediately
     * @returns Promise that resolves when all master data is loaded
     */
    const loadAllMasterData = async (): Promise<void> => {
        // Skip if already loaded or currently loading
        if (isLoaded.value || isLoading.value) {
            return;
        }

        isLoading.value = true;
        loadError.value = '';

        try {
            // Load all master data in parallel for better performance
            const [directOrderStatusData, couriers] = await Promise.all([
                MasterDataApiCaller.getMasterDirectOrderStatus(),
                MasterDataApiCaller.getMasterCourierList()
            ]);

            // Update reactive refs with their corresponding master data
            directOrderStatus.value = directOrderStatusData;
            courierList.value = couriers;

            isLoaded.value = true;

            console.log('✅ Master data loaded successfully:', {
                directOrderStatus: Object.keys(directOrderStatusData).length,
                courierList: couriers.length
            });
        } catch (error) {
            loadError.value = error instanceof Error ? error.message : 'Failed to load master data';
            console.error('❌ Failed to load master data:', error);

            // Set empty values as fallback
            directOrderStatus.value = {};
            courierList.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Force reload all master data
     * Use this if you need to refresh master data
     */
    const reloadMasterData = async (): Promise<void> => {
        isLoaded.value = false;
        await loadAllMasterData();
    };

    /**
     * Clear all cached master data
     * Useful for testing or when user logs out
     */
    const clearMasterData = (): void => {
        directOrderStatus.value = {};
        courierList.value = [];
        isLoaded.value = false;
        isLoading.value = false;
        loadError.value = '';
    };

    return {
        // Master data (reactive refs)
        directOrderStatus,
        courierList,

        // Loading state
        isLoading,
        isLoaded,
        loadError,

        // Methods
        loadAllMasterData,
        reloadMasterData,
        clearMasterData
    };
}
